<template>
  <div v-if="isCaptionVisible" class="transaction-form-entry__caption">
    <div class="transaction-form-entry__caption-text">{{ entryTitle }}</div>
    <div v-if="entryDescription" class="transaction-form-entry__question">
      <QuestionMarkCircleIcon
        v-tooltip="entryDescription"
        class="transaction-form-entry__question-icon"
      />
    </div>
  </div>
  <div v-if="isAccountVisible" class="transaction-form-entry__asset">
    <component
      :is="accountComponent"
      ref="accountFieldRef"
      :data-refid="`transactionFormAccount${index}`"
      v-bind="{
        instance,
        label: accountLabel,
        readonly,
      }"
      show-summary-button
      @ready="(el: HTMLInputElement) => (accountFieldRef = el)"
    />
  </div>
  <component
    :is="amountComponent"
    v-if="isAmountVisible"
    ref="amountFieldRef"
    :data-refid="`transactionFormAmount${index}`"
    v-bind="{
      instance,
      label: amountLabel,
      readonly,
    }"
    @ready="(el: HTMLInputElement) => (amountFieldRef = el)"
  />
  <div v-if="isAssetVisible" class="transaction-form-entry__asset">
    <component
      :is="assetComponent"
      ref="assetFieldRef"
      :data-refid="`transactionFormAsset${index}`"
      v-bind="{
        instance,
        label: assetLabel,
        readonly,
      }"
      show-summary-button
      @ready="(el: HTMLInputElement) => (assetFieldRef = el)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue'

import { TransactionClass, TransactionComputedEntry } from '@types'
import {
  TRANSACTION_ENTRY_FIELD,
  TRANSACTION_ENTRY_FIELD_LABEL,
} from '../utils/const'

import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid'

type Props = {
  instance: TransactionClass
  index: number
  readonly?: boolean
}

const props = defineProps<Props>()

defineExpose({
  focus() {
    if (!(props.instance[entryKey.value] as TransactionComputedEntry)().value?.account_id) {
      accountFieldRef.value?.focus?.()
    } else if (!(props.instance[entryKey.value] as TransactionComputedEntry)().value?.amount) {
      amountFieldRef.value?.focus?.()
    } else if (!(props.instance[entryKey.value] as TransactionComputedEntry)().value?.asset_id) {
      assetFieldRef.value?.focus?.()
    }
  },
})

const accountFieldRef = ref<HTMLInputElement>()
const amountFieldRef = ref<HTMLInputElement>()
const assetFieldRef = ref<HTMLInputElement>()

const entryKey = computed(() => `entry${props.index}` as keyof TransactionClass)

const entrySettings = computed(
  () => props.instance.settings?.entries[props.index - 1],
)
provide('entrySettings', entrySettings)
const entryTitle = computed(() => entrySettings.value?.title)
const entryDescription = computed(() => entrySettings.value?.description)

const isAccountEmpty = computed(() => {
  const key = `emptyAccountId${props.index}` as keyof TransactionClass
  return props.instance[key]
})
const isAmountEmpty = computed(() => {
  const key = `emptyAmount${props.index}` as keyof TransactionClass
  return props.instance[key]
})
const isAssetEmpty = computed(() => {
  const key = `emptyAssetId${props.index}` as keyof TransactionClass
  return props.instance[key]
})

const accountComponent = computed(() => {
  const key = `getFormAccountId${props.index}` as keyof TransactionClass
  return (props.instance[key] as TransactionClass['getFormAccountId1'])()
})
const amountComponent = computed(() => {
  const key = `getFormAmount${props.index}` as keyof TransactionClass
  return (props.instance[key] as TransactionClass['getFormAmount1'])()
})
const assetComponent = computed(() => {
  const key = `getFormAssetId${props.index}` as keyof TransactionClass
  return (props.instance[key] as TransactionClass['getFormAssetId1'])()
})

const accountLabel = computed(
  () =>
    entrySettings.value?.account?.title ||
    TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ACCOUNT_ID],
)
const amountLabel = computed(
  () =>
    entrySettings.value?.amount?.title ||
    TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.AMOUNT],
)
const assetLabel = computed(
  () =>
    entrySettings.value?.asset?.title ||
    TRANSACTION_ENTRY_FIELD_LABEL[TRANSACTION_ENTRY_FIELD.ASSET_ID],
)
const isAccountVisible = computed(() => !isAccountEmpty.value)
const isAmountVisible = computed(() => !isAmountEmpty.value)
const isAssetVisible = computed(() => !isAssetEmpty.value)
const isCaptionVisible = computed(
  () => isAccountVisible.value || isAmountVisible.value || isAssetVisible.value,
)
</script>

<style scoped>
.transaction-form-entry {
  &__caption {
    @apply flex justify-between;
    @apply mb-2 py-1;
    @apply rounded;
    @apply font-medium text-sm sm:text-xs;
    @apply bg-indigo-50 dark:bg-indigo-900;
    @apply text-indigo-600 dark:text-indigo-400;
  }

  &__caption-text {
    @apply flex justify-center;
    @apply w-full;
    @apply ml-3;
  }

  &__question {
    @apply invisible group-hover:visible;
    @apply mr-1;
  }

  &__question-icon {
    @apply h-4 w-4;
    @apply text-indigo-400 dark:text-indigo-600;
  }

  &__asset {
    @apply flex items-end gap-x-1;

    :deep() > * {
      @apply first:flex-auto;
    }
  }
}
</style>
